import styled from "styled-components";
import { useState, useEffect } from "react";
import PrimaryButton from "../../../../../components/FormControls/PrimaryButton";
import SecondaryButton from "../../../../../components/FormControls/SecondaryButton";
import NarrowGrid from "../../../../../components/NarrowGrid";
import FormStringInput from "../../../../../components/FormControls/FormStringInput";
import request from "../../../../../api/request";
import { NotificationManager } from 'react-notifications';

const StyledVATCodes = styled.div`
    margin-left: 30px;
`;

const WhiteBoard = styled.div`
    background-color: white;
    width: 750px;
    box-sizing: border-box;
    min-height: 460px;
    padding: 30px;
    margin-bottom: 30px;
    color: ${(props) => props.theme.color.gray};
`;

function VATCodes(props) {

    const [loadedPage, setLoadedPage] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [vatCodes, setVATCodes] = useState([]);
    const [selectedVATCode, setSelectedVATCode] = useState(null);
    const [selectedVATCodeId, setSelectedVATCodeId] = useState(null);
    const [isAddMode, setIsAddMode] = useState(false);
    const [gridColumns, setGridColumns] = useState([
        {id: 'Name', type: 'String', width: 150, textAlign: 'left'},
        {id: 'Code', type: 'String', width: 70, textAlign: 'center'},
    ]);
    const [gridData, setGridData] = useState({groups: [''], items: [[]]});
    const [vatCode, setVATCode] = useState('');
    const [vatDescription, setVATDescription] = useState('');
    const [vatRate, setVATRate] = useState('0.00');
    const [needsValidation, setNeedsValidation] = useState(false);
    const [isAddingDuplicatedItem, setIsAddingDuplicatedItem] = useState(false);

    const onSelectGridItem = (item) => {
        setSelectedVATCodeId(item.Id);
        setSelectedVATCode(item);
    }
    
    const fetchVATCodes = async (vatCodeToSelect) => {
        try {
            let itemList = [];

            const resp = (await request('database', 'fetchVATCodes'));
            if (resp.ok) {
                resp.data.map((item, index) => {
                    itemList.push(item);
                });
                setVATCodes(itemList);
                rebuildGridData(itemList, vatCodeToSelect);
            } else {
                console.log(resp.data);
                NotificationManager.error(resp.data);
                return false;
            }
        } catch (e) {
            console.log('Failed to fetch vat codes: ', e);
            return false;
        }
        return true;
    }

    const onDeleteVATCode = async () => {
        if (selectedVATCode) {
            setIsDeleting(true);
            let deleteData = {};
            deleteData.VATCode = selectedVATCode.VATCode;

            const resp = (await request('database', 'deleteVATCode', [deleteData]));
            if (resp.ok) {
                setSelectedVATCode(null);
                setSelectedVATCodeId(null);
                fetchVATCodes();
                NotificationManager.success('Successfully deleted VAT code');
            } else
            {
                console.log(resp.data);
                NotificationManager.error(resp.data);
            }
            setIsDeleting(false);
        }
    }

    const floatToString = (val) => {
        return val.toFixed(2);
    }

    const onSaveChanges = async () => {
        if (isAddMode) { // adding new item
            if (vatDescription === '' || vatCode === '' || isNaN(parseFloat(vatRate))) {
                setNeedsValidation(true);
            } else {
                // check duplicated code
                if (gridData.items[0].length > 0 && (gridData.items[0].find((itm) => itm.VATCode.replace(/\s/g, '') === vatCode.replace(/\s/g, '')))) {
                    setIsAddingDuplicatedItem(true);
                } else {
                    setIsSaving(true);
                    let saveData = {};
                    saveData.VATCode = vatCode;
                    saveData.VATDescription = vatDescription;
                    saveData.VATRate = parseFloat(vatRate);

                    const resp = (await request('database', 'createVATCode', [saveData]));
                    if (resp.ok) {
                        fetchVATCodes(vatCode);
                        NotificationManager.success('Successfully added VAT code');
                    } else
                    {
                        console.log(resp.data);
                        NotificationManager.error(resp.data);
                    }
                    setIsSaving(false);
                    setIsAddMode(false);
                }
            }
        } else {
            if (selectedVATCode && vatCode === '' || isNaN(parseFloat(vatRate))) {
                setNeedsValidation(true);
            } else {
                setIsSaving(true);
                let saveData = {};
                saveData.VATCode = vatCode;
                saveData.VATDescription = vatDescription;
                saveData.VATRate = parseFloat(vatRate);

                const resp = (await request('database', 'updateVATCode', [saveData]));
                if (resp.ok) {
                    fetchVATCodes(vatCode);
                    NotificationManager.success('Successfully updated VAT code');
                } else
                {
                    console.log(resp.data);
                    NotificationManager.error(resp.data);
                }
                setIsSaving(false);
            }
        }
    }

    const rebuildGridData = (items, vatCodeToSelect) => {
        if (items && items.length > 0) {
            let itemList = [];
            items.map((item, index) => {
                let gridItem = {Id: index, Name: item.VATDescription, Code: item.VATCode, ...item};
                itemList.push(gridItem);
                if (vatCodeToSelect && item.VATCode === vatCodeToSelect) {
                    setSelectedVATCodeId(index);
                    setSelectedVATCode(gridItem);
                }
            });
            setGridData({groups: [''], items: [itemList]});
        }
    }

    useEffect(() => {
        if (!loadedPage) {
            props.setPageTitle('VAT codes');
            fetchVATCodes();
            setLoadedPage(true);
        }
    }, []);

    useEffect(() => {
        if (isAddMode) {
            setNeedsValidation(false);
            setVATDescription('');
            setVATCode('');
            setVATRate('0.00');
        } else if (selectedVATCode) {
            setNeedsValidation(false);
            setVATDescription(selectedVATCode.VATDescription);
            setVATCode(selectedVATCode.VATCode);
            setVATRate(floatToString(selectedVATCode.VATRate));
        }
    }, [isAddMode]);

    useEffect(() => {
        if (selectedVATCode) {
            setNeedsValidation(false);
            setVATDescription(selectedVATCode.VATDescription);
            setVATCode(selectedVATCode.VATCode);
            setVATRate(floatToString(selectedVATCode.VATRate));
        } else {
            setNeedsValidation(false);
            setVATDescription('');
            setVATCode('');
            setVATRate('0.00');
        }
    }, [selectedVATCode]);

    useEffect(() => {
        setIsAddingDuplicatedItem(false);
        setNeedsValidation(false);
    }, [selectedVATCode, isAddMode]);

    return (
        <StyledVATCodes>
            <WhiteBoard>
                <div style={{display:'flex'}}>
                    <div>
                        <NarrowGrid id='generaltables_vatcodes_grid' gridColumns={gridColumns} gridData={gridData} disabled={isAddMode} itemIdToSelect={selectedVATCodeId} onSelectGridItem={onSelectGridItem} gridStyle={{width:220, height:350, marginTop:20}}></NarrowGrid>
                        <div style={{marginTop:20}}>
                            {selectedVATCodeId !== null && !isAddMode && <SecondaryButton caption='Delete' isLoading={isDeleting} onClick={() => onDeleteVATCode()} style={{width:75, height:25}}></SecondaryButton>}
                            {isAddMode && <SecondaryButton caption='Cancel' onClick={() => setIsAddMode(false)} style={{width:75, height:25, float:'right'}}></SecondaryButton>}
                            {!isAddMode && <PrimaryButton caption='Create new' onClick={() => setIsAddMode(true)} style={{width:100, height:25, float:'right'}}></PrimaryButton>}
                        </div>
                    </div>
                    <div style={{marginLeft:30}}>
                        <FormStringInput label='Code' value={vatCode} onChange={(val) => setVATCode(val)} inputStyle={{width:100, height:35}} validated={needsValidation && vatCode === '' ? false : undefined} disabled={!isAddMode} ></FormStringInput>
                        <FormStringInput label='Description' value={vatDescription} onChange={(val) => setVATDescription(val)} inputStyle={{width:290, height:35}} validated={needsValidation && vatDescription === '' ? false : undefined} disabled={!isAddMode && !selectedVATCode}></FormStringInput>
                        <FormStringInput label='VAT rate' value={vatRate} disabled={!isAddMode && !selectedVATCode} validated={needsValidation && isNaN(parseFloat(vatRate)) ? false : undefined} onChange={(val) => setVATRate(val)} inputStyle={{width:100, height:35}}></FormStringInput>
                    {isAddingDuplicatedItem && <div style={{marginTop:15, width:280, fontSize:12, fontWeight:700, color:'#dc3545'}}>* Code is duplicated.</div>}
                    </div>
                </div>
            </WhiteBoard>
            <div>
                <PrimaryButton caption={isAddMode ? 'Add VAT code' : 'Save changes'} isLoading={isSaving} onClick={() => onSaveChanges()} disabled={isAddMode || selectedVATCode ? false : true} style={{width:150, float:'right'}}></PrimaryButton>
            </div>
        </StyledVATCodes>
    )
}

export default VATCodes;